class CartRemoveButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.closest('cart-items').updateQuantity(this.dataset.index, 0);
    });
  }
}
customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    // helper variables
    this.cartTotalValue = 0;
    this.cartTotalDiscount = 0;
    this.cartOriginalPrice = 0;

    // Race condition guards
    this.isProcessingBundle = false; // Prevent concurrent bundle operations
    this.isProcessingCheckout = false; // Prevent multiple checkout attempts
    this.isUpdatingCart = false; // Prevent concurrent cart updates
    this.pendingCartUpdate = null; // Queue for cart updates
    this.checkoutButtonIntercepted = false; // Prevent multiple event listeners

    // elements
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.currentItemCount = Array.from(this.querySelectorAll('[name="updates[]"]')).reduce((total, quantityInput) => total + parseInt(quantityInput.value), 0);

    // events
    this.debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, 300);
    this.addEventListener('change', this.debouncedOnChange.bind(this));

    // Init items from API to prevent cart content missmatch on browser back/forward commands
    this.renderItems();

    // dev events
    document.addEventListener('ajaxProduct:added', async (event) => {
      // Check if we should skip opening the drawer (for upsell products)
      const skipDrawer = event.detail && event.detail.skipDrawer;
      await this.renderAndShowItems(skipDrawer);
      // After cart is updated with new items, check for bundles
      setTimeout(() => {
        this.checkAndApplyBundleDiscount();
      }, 300);
    })

    // Bundle removal handler - use event delegation
    this.addEventListener('click', (event) => {
      const removeBtn = event.target.closest('.bundle-remove-btn');
      if (removeBtn) {
        event.preventDefault();
        event.stopPropagation();
        this.handleBundleRemoval(removeBtn);
      }
    });

    // Free shipping check onload
    this.updateShippingNotification('cart-shipping-notification');
    
    // Check if bundle items need discount applied when cart page loads or drawer opens
    const isCartPage = window.location.pathname === '/cart';
    const isDrawer = this.closest('[data-drawer]') !== null;
    
    // IMPORTANT: Don't run bundle checking on homepage or collection pages
    // Only run on cart page OR when cart drawer is actually being used
    const isHomepage = window.location.pathname === '/' || window.location.pathname === '/index';
    const isCollectionPage = window.location.pathname.includes('/collections/') && 
                             !window.location.pathname.includes('/products/');
    const isProductPage = window.location.pathname.includes('/products/');
    
    // Skip bundle checking on pages where cart is not the focus
    const shouldSkipBundleCheck = isHomepage || isCollectionPage;
    
    if ((isCartPage || isDrawer) && !shouldSkipBundleCheck) {
      // Wait for cart to be fully loaded before checking bundles
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.checkAndApplyBundleDiscount();
        });
      } else {
        // Cart already loaded, check immediately
        this.checkAndApplyBundleDiscount();
      }
      
      // Intercept checkout button to ensure bundle discount is applied before checkout
      this.interceptCheckoutButton();
      
      // Also listen for drawer open events to intercept checkout button and run bundle detection
      if (isDrawer) {
        document.addEventListener('drawer:opened', () => {
          // Re-intercept checkout button when drawer opens (in case it was dynamically added)
          setTimeout(() => {
            this.interceptCheckoutButton();
            // Also check and apply bundle discount when drawer opens (cart may have changed)
            this.checkAndApplyBundleDiscount();
          }, 100);
        });
      }
      
      // Also listen for cart updates (when items are added/removed) to re-check bundles
      document.addEventListener('cart:updated', () => {
        // Small delay to ensure cart DOM is updated
        setTimeout(() => {
          this.checkAndApplyBundleDiscount();
        }, 200);
      });
    } else if (shouldSkipBundleCheck) {
      
      
      // Still intercept checkout button for when drawer is opened from these pages
      if (isDrawer) {
        document.addEventListener('drawer:opened', () => {
          setTimeout(() => {
            this.interceptCheckoutButton();
            this.checkAndApplyBundleDiscount();
          }, 100);
        });
      }
    }
  }
  
  /**
   * Helper: Show loading indicator for discount application
   * Uses the store's global loading component styles
   */
  showDiscountLoading() {
    // Only show loading on the cart page
    const isCartPage = window.location.pathname === '/cart';
    if (!isCartPage) {
      
      return;
    }
    
    // Check if loading indicator already exists
    let loadingIndicator = document.getElementById('discount-application-loading');
    
    if (!loadingIndicator) {
      // Create loading indicator using store's loading component pattern
      loadingIndicator = document.createElement('div');
      loadingIndicator.id = 'discount-application-loading';
      loadingIndicator.className = 'discount-application-loading';
      loadingIndicator.innerHTML = `
        <div class="discount-loading-content">
          <div class="loading-overlay__spinner">
            <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </div>
        </div>
      `;
    }
    
    // Show loading indicator
    if (!document.body.contains(loadingIndicator)) {
      document.body.appendChild(loadingIndicator);
    }
    loadingIndicator.classList.remove('is-hidden');
  }
  
  /**
   * Helper: Hide loading indicator for discount application
   */
  hideDiscountLoading() {
    const loadingIndicator = document.getElementById('discount-application-loading');
    if (loadingIndicator) {
      loadingIndicator.classList.add('is-hidden');
      loadingIndicator.classList.remove('is-visible');
    }
  }
  
  /**
   * Helper: Wait for discount to be applied in cart (with polling)
   */
  async waitForDiscountApplication(discountCode, maxAttempts = 5, delayMs = 300) {
    // Show loading indicator
    this.showDiscountLoading();
    
    try {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const cartResponse = await fetch('/cart.js');
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          
          // Check if discount is actually applying
          if (cart.discount_applications && cart.discount_applications.length > 0) {
            this.hideDiscountLoading();
            return { success: true, cart };
          }
          
          // Check if discount code is present (but not applying yet)
          if (cart.discount_codes && cart.discount_codes.length > 0) {
            const appliedCode = cart.discount_codes[0].code;
            if (appliedCode.toUpperCase() === discountCode.toUpperCase() || 
                appliedCode.toUpperCase().includes('BDL-')) {
              // Code is present, but not applying yet - wait and retry
              if (attempt < maxAttempts - 1) {
                await new Promise(resolve => setTimeout(resolve, delayMs));
                continue;
              }
            }
          }
        }
        
        if (attempt < maxAttempts - 1) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
      
      // Final check
      const finalCartResponse = await fetch('/cart.js');
      if (finalCartResponse.ok) {
        const finalCart = await finalCartResponse.json();
        const result = { 
          success: finalCart.discount_applications && finalCart.discount_applications.length > 0,
          cart: finalCart 
        };
        this.hideDiscountLoading();
        return result;
      }
      
      this.hideDiscountLoading();
      return { success: false, cart: null };
      
    } catch (error) {
      // Hide loading on error
      this.hideDiscountLoading();
      console.error('Cart: Error waiting for discount application:', error);
      throw error;
    }
  }
  
  /**
   * Helper: Wait for DOM element to appear
   */
  async waitForDOMElement(selector, timeoutMs = 2000) {
    // Check if element already exists
    const existingElement = document.querySelector(selector);
    if (existingElement) {
      return existingElement;
    }
    
    // Wait for element to appear
    return new Promise((resolve, reject) => {
      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) {
          observer.disconnect();
          resolve(element);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
      
      // Also check immediately in case element appears before observer is set up
      const quickCheck = setInterval(() => {
        const element = document.querySelector(selector);
        if (element) {
          clearInterval(quickCheck);
          observer.disconnect();
          resolve(element);
        }
      }, 50);
      
      setTimeout(() => {
        clearInterval(quickCheck);
        observer.disconnect();
        // Return null instead of rejecting - caller can handle
        const element = document.querySelector(selector);
        resolve(element || null);
      }, timeoutMs);
    });
  }
  
  /**
   * Helper: Wait for cart update to complete (verify by fetching cart)
   */
  async waitForCartUpdate(initialItemCount, timeoutMs = 2000) {
    const startTime = Date.now();
    
    while (Date.now() - startTime < timeoutMs) {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        // Cart has updated if item count changed or response is different
        return cart;
      }
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    // Return current cart even if timeout
    const cartResponse = await fetch('/cart.js');
    return cartResponse.ok ? await cartResponse.json() : null;
  }
  
  /**
   * Manage checkout button state (disable/enable during bundle processing)
   */
  setCheckoutButtonState(disabled, loadingText = null) {
    // Find all checkout buttons (cart page and drawer)
    const checkoutButtons = document.querySelectorAll('button[name="checkout"], button[type="submit"][name="checkout"], [data-checkout-button]');
    
    checkoutButtons.forEach(button => {
      if (disabled) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (loadingText) {
          button.setAttribute('aria-label', loadingText);
        }
        
        // Just add the processing class - don't change the text
        button.classList.add('checkout-button-processing');
      } else {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.removeAttribute('aria-label');
        
        // Remove the processing class - text stays the same
        button.classList.remove('checkout-button-processing');
      }
    });
  }
  
  /**
   * Wait for bundle processing to complete
   */
  async waitForBundleProcessing(maxWaitMs = 10000) {
    const startTime = Date.now();
    
    while (this.isProcessingBundle && (Date.now() - startTime < maxWaitMs)) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    if (this.isProcessingBundle) {
      console.warn('Cart: Bundle processing timeout - proceeding anyway');
      return false; // Timeout
    }
    
    return true; // Completed
  }
  
  /**
   * Check if bundle items need discount applied and apply it
   */
  async checkAndApplyBundleDiscount() {
    // Safety check: Don't run on homepage or collection pages
    const isHomepage = window.location.pathname === '/' || window.location.pathname === '/index';
    const isCollectionPage = window.location.pathname.includes('/collections/') && 
                             !window.location.pathname.includes('/products/');
    
    if (isHomepage || isCollectionPage) {
      
      return;
    }
    
    // Prevent concurrent execution
    if (this.isProcessingBundle) {
      return;
    }
    
    this.isProcessingBundle = true;
    
    // Disable checkout button while processing
    this.setCheckoutButtonState(true, 'Processing bundle...');
    
    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        return;
      }
      
      const cart = await cartResponse.json();
      
      // Check if there are items that could be grouped or regrouped
      // This includes:
      // 1. Items without bundle properties (new items)
      // 2. Items with bundle properties (existing bundles that might qualify for higher tier)
      const itemsWithoutBundleProps = cart.items.filter(item => 
        !item.properties || (!item.properties._bundle_id && !item.properties._bundle_group)
      );
      
      const itemsWithBundleProps = cart.items.filter(item => 
        item.properties && (item.properties._bundle_id || item.properties._bundle_group)
      );
      
      // IMPORTANT: Before grouping, check if there are any sale items in the cart
      // that would be in the same collection as potential bundle items.
      // If so, we should not apply discounts after grouping.
      
      // Check if any items without bundle properties are on sale
      const hasSaleItemWithoutBundle = itemsWithoutBundleProps.some(item => this.checkItemOnSale(item));
      
      // If there are items without bundle properties, try to detect and group them
      // OR if there are both bundle items and non-bundle items of the same product, try to regroup
      if (itemsWithoutBundleProps.length >= 2 || 
          (itemsWithoutBundleProps.length >= 1 && itemsWithBundleProps.length >= 1)) {
        // Update button text to show what's happening
        this.setCheckoutButtonState(true, 'Detecting bundles...');
        
        // Pass flag to detectAndGroupBundleItems to skip discount if sale items present
        const grouped = await this.detectAndGroupBundleItems(cart, { skipDiscountIfSaleItems: hasSaleItemWithoutBundle });
        
        if (grouped) {
          // Items were grouped, update cart sections smoothly without full page refresh
          // Wait for discount to propagate using polling instead of fixed delay
          this.setCheckoutButtonState(true, 'Applying discount...');
          await this.waitForCartUpdate(cart.item_count);
          await this.updateCartAfterGrouping();
          // Re-enable button after grouping completes
          this.setCheckoutButtonState(false);
          return;
        }
      }
      
      // Find items with bundle properties
      const bundleItems = cart.items.filter(item => 
        item.properties && (item.properties._bundle_id || item.properties._bundle_group)
      );
      
      if (bundleItems.length === 0) {
        // No bundle items, but check if discount should be removed
        const hasDiscount = cart.discount_codes && cart.discount_codes.length > 0;
        if (hasDiscount) {
          // No bundle items but discount is applied - remove it
          if (window.bundleDiscountAPI && typeof window.bundleDiscountAPI.removeDiscountCode === 'function') {
            const discountCode = cart.discount_codes[0]?.code;
            if (discountCode) {
              
              await window.bundleDiscountAPI.removeDiscountCode(discountCode);
              await this.updateCartAfterGrouping();
            }
          }
        }
        return;
      }
      
      // Get bundle info from first item
      const firstItem = bundleItems[0];
      const bundleGroupId = firstItem.properties._bundle_group || firstItem.properties._bundle_id || '';
      const bundleTier = firstItem.properties._bundle_tier || '';
      
      if (!bundleGroupId || !bundleTier) {
        return;
      }
      
      // Check if any bundle item is on sale (exclude entire bundle if any item is on sale)
      const hasSaleItem = bundleItems.some(item => {
        const isOnSale = this.checkItemOnSale(item);
        if (isOnSale) {
          console.log('Cart: ✅ Found sale item in bundle:', {
            product: item.product_title,
            variant: item.variant_title,
            original_price: item.original_price,
            final_price: item.final_price
          });
        }
        return isOnSale;
      });
      
      // IMPORTANT: Before applying discount, check if there are any sale items in the cart
      // that are in the same collection as the bundle items.
      // The discount code applies to the entire collection, so if there are sale items
      // in the same collection, they would also get the discount (which we don't want).
      
      // Get bundle config to find collection handle
      const bundleConfigData = this.getBundleConfigData();
      let bundleCollectionHandle = null;
      if (bundleConfigData && bundleConfigData.products) {
        const productConfig = bundleConfigData.products.find(p => 
          p.variantId === firstItem.variant_id
        );
        if (productConfig && productConfig.bundleConfig) {
          bundleCollectionHandle = productConfig.bundleConfig.collectionHandle;
        }
      }
      
      // Check if any non-bundle items in the cart are:
      // 1. In the same collection (same product or same bundle group)
      // 2. On sale
      const nonBundleItems = cart.items.filter(item => {
        const isBundleItem = item.properties && (item.properties._bundle_id || item.properties._bundle_group);
        return !isBundleItem;
      });
      
      // Check if any non-bundle items from the same product/collection are on sale
      const hasSaleItemInSameCollection = nonBundleItems.some(item => {
        // Same product = same sibling collection
        if (item.product_title === firstItem.product_title) {
          return this.checkItemOnSale(item);
        }
        // Also check by bundle group ID if available in config
        if (bundleCollectionHandle && bundleConfigData) {
          const itemConfig = bundleConfigData.products.find(p => 
            p.variantId === item.variant_id
          );
          if (itemConfig && itemConfig.bundleConfig && 
              itemConfig.bundleConfig.collectionHandle === bundleCollectionHandle) {
            return this.checkItemOnSale(item);
          }
        }
        return false;
      });
      
      // Check if discount is already applied
      const hasDiscount = cart.discount_codes && cart.discount_codes.length > 0;
      
      // If bundle has sale items OR there are sale items in same collection, don't apply/remove discount
      if (hasSaleItem || hasSaleItemInSameCollection) {
        if (hasSaleItem) {
          
        }
        if (hasSaleItemInSameCollection) {
          console.log('Cart: Sale items in same collection:', nonBundleItems.filter(item => {
            if (item.product_title === firstItem.product_title) {
              return this.checkItemOnSale(item);
            }
            return false;
          }).map(item => ({
            product: item.product_title,
            variant: item.variant_title
          })));
        }
        
        // If discount is already applied, remove it
        if (hasDiscount) {
          if (window.bundleDiscountAPI && typeof window.bundleDiscountAPI.removeDiscountCode === 'function') {
            const discountCode = cart.discount_codes[0]?.code;
            if (discountCode) {
              await window.bundleDiscountAPI.removeDiscountCode(discountCode);
              await this.updateCartAfterGrouping();
            }
          }
        }
        return;
      }
      
      // If discount is already applied and conditions are met, keep it
      if (hasDiscount) {
        return;
      }
      
      // Generate discount code from bundle properties (matching serverless function format)
      const bundleGroupIdFormatted = bundleGroupId
        .toString()
        .toUpperCase()
        .substring(0, 20);
      
      const discountCode = `BDL-${bundleTier}P-${bundleGroupIdFormatted}`;
      
      // Apply discount code
      if (window.bundleDiscountAPI && typeof window.bundleDiscountAPI.applyDiscountCode === 'function') {
        const result = await window.bundleDiscountAPI.applyDiscountCode(discountCode);
        if (result) {
          // Wait for discount to propagate using polling instead of fixed delay
          const discountResult = await this.waitForDiscountApplication(discountCode, 5, 300);
          if (discountResult.success) {
            await this.updateCartAfterGrouping();
          } else {
            console.warn('Cart: Discount code applied but not verified, updating anyway...');
            await this.updateCartAfterGrouping();
          }
        } else {
          console.warn(`Cart: Failed to apply discount code: ${discountCode}`);
        }
      } else {
        console.warn('Cart: bundleDiscountAPI not available');
      }
      
    } catch (error) {
      console.error('Cart: Error checking/applying bundle discount:', error);
    } finally {
      // Always release the lock and re-enable checkout button
      this.isProcessingBundle = false;
      this.setCheckoutButtonState(false);
    }
  }
  

  /**
   * Helper: Get bundle config data from Liquid-generated JSON
   */
  getBundleConfigData() {
    try {
      const configScript = document.getElementById('cart-bundle-config');
      if (!configScript) return null;
      return JSON.parse(configScript.textContent);
    } catch (error) {
      console.warn('Cart: Error parsing bundle config data:', error);
      return null;
    }
  }

  /**
   * Helper: Check if item is on sale using data from Liquid (which has access to variant.compare_at_price)
   */
  checkItemOnSale(item) {
    // First, try to get sale status from bundle config JSON (generated by Liquid)
    const bundleConfigData = this.getBundleConfigData();
    if (bundleConfigData && bundleConfigData.products) {
      const productConfig = bundleConfigData.products.find(p => 
        p.variantId === item.variant_id
      );
      if (productConfig && productConfig.isOnSale === true) {
        return true;
      }
    }
    
    // Fallback: Check if original_price > final_price (item is on sale)
    const originalPrice = parseFloat(item.original_price || item.price || 0);
    const finalPrice = parseFloat(item.final_price || item.price || 0);
    
    // Item is on sale if original_price > final_price
    if (originalPrice > 0 && finalPrice > 0 && originalPrice > finalPrice + 0.01) {
      return true;
    }
    
    // Also check if item has sale indicator in properties
    if (item.properties && item.properties._is_on_sale === 'true') {
      return true;
    }
    
    return false;
  }

  /**
   * Detect and group bundle-eligible items that were manually added
   * Note: This is an internal function called from checkAndApplyBundleDiscount() which already has the guard
   * @param {Object} options - Optional parameters
   * @param {boolean} options.skipDiscountIfSaleItems - If true, skip applying discount if sale items are detected
   */
  async detectAndGroupBundleItems(cart, options = {}) {
    try {
     
      
      // Group items by normalized product title (including items with existing bundle properties)
      const productGroups = {};
      
      // Check all items for sale status (using Liquid-generated data)
      const saleStatusChecks = cart.items.map(item => this.checkItemOnSale(item));
      
      for (let i = 0; i < cart.items.length; i++) {
        const item = cart.items[i];
        const isOnSale = saleStatusChecks[i];
        
        if (isOnSale) {         
          continue;
        }
        
        // Include ALL items (both with and without bundle properties) for regrouping
        // Normalize product title to group variants
        const productTitle = item.product_title || '';
        const normalizedKey = productTitle
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')
          .replace(/--+/g, '-');
        
        if (!normalizedKey) continue;
        
        if (!productGroups[normalizedKey]) {
          productGroups[normalizedKey] = {
            title: productTitle,
            items: [],
            hasExistingBundle: false
          };
        }
        
        productGroups[normalizedKey].items.push(item);
        
        // Track if this group has existing bundle items
        if (item.properties && (item.properties._bundle_id || item.properties._bundle_group)) {
          productGroups[normalizedKey].hasExistingBundle = true;
        }
      }
      
      // Find groups with 2+ items (bundle eligible)
      // BUT exclude groups that contain ANY sale items (even if mixed with non-sale items)
      const bundleGroups = Object.values(productGroups).filter(group => {
        // First check: Does this group contain ANY sale items?
        // Use the pre-computed sale status checks
        const hasSaleItemInGroup = group.items.some(item => {
          const itemIndex = cart.items.findIndex(cartItem => 
            cartItem.variant_id === item.variant_id && 
            cartItem.key === item.key
          );
          
          if (itemIndex !== -1 && saleStatusChecks[itemIndex]) {
            return true;
          }
          
          return false;
        });
        
        // If group has ANY sale items, exclude it from bundling
        if (hasSaleItemInGroup) {
          return false;
        }
        
        const totalQuantity = group.items.reduce((sum, item) => sum + (item.quantity || 1), 0);
        // Include if: has 2+ items total, OR has existing bundle (for regrouping)
        return totalQuantity >= 2 || group.hasExistingBundle;
      });
      
      if (bundleGroups.length === 0) {
        return false;
      }
      
      // Process the first bundle group
      const bundleGroup = bundleGroups[0];
      const items = bundleGroup.items;
      
      // Calculate total quantity (sum of all item quantities, including existing bundle items)
      const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 1), 0);
      
      const existingBundleCount = items.filter(item => 
        item.properties && (item.properties._bundle_id || item.properties._bundle_group)
      ).length;
      
      // Try to find bundle configuration by testing discount codes
      // Since we can't get metafields from JSON API, we'll try common discount code patterns
      // Use total quantity to determine the correct bundle tier
      const quantity = totalQuantity;
      const bundleConfig = await this.findBundleConfigByTestingDiscountCodes(items[0], quantity);
      
      if (!bundleConfig) {
        return false;
      }
      
      // Use the tier from config (this is the tier quantity for the discount code)
      const applicableTier = bundleConfig.tier;
      
      // Note: We use the tier quantity for discount code, but group items based on actual quantity
      
      // Extract sizes and colors from items
      const extractSize = (variantTitle) => {
        if (!variantTitle) return '';
        const parts = variantTitle.split(' / ');
        return parts.length > 1 ? parts[1].trim() : variantTitle.trim();
      };
      
      const extractColor = (item) => {
        // Try to get color from variant title or properties
        const variantTitle = item.variant_title || '';
        const parts = variantTitle.split(' / ');
        if (parts.length > 0) {
          return parts[0].trim();
        }
        // Check properties
        for (const key in item.properties || {}) {
          if (key.toLowerCase().includes('color')) {
            return item.properties[key];
          }
        }
        return '';
      };
      
      const sizes = items.map(item => extractSize(item.variant_title)).filter(Boolean);
      const colors = items.map(item => extractColor(item)).filter(Boolean);
      
      // Calculate bundle price - use tier price from config
      // Regular price per item
      const regularPrice = items[0].price || items[0].final_price || 0;
      const regularTotal = regularPrice * quantity;
      
      // Use bundle price from config (this is the total price for the tier)
      const bundlePrice = bundleConfig.price || (regularTotal * 0.825); // Fallback if price not in config
      const itemPrice = (bundlePrice / quantity).toFixed(2);
      
      // Generate bundle ID
      const bundleId = `bundle_${Date.now()}`;
      const bundleGroupId = bundleConfig.groupId;
      const bundleTier = applicableTier.toString();
      
      // Prepare items to re-add with bundle properties
      // Include ALL items (both existing bundle items and new items) for regrouping
      const itemsToReAdd = [];
      const linesToRemove = [];
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const itemSize = extractSize(item.variant_title);
        const itemColor = extractColor(item);
        
        // Create new properties, preserving non-bundle properties but updating bundle properties
        const newProperties = { ...(item.properties || {}) };
        
        // Update bundle properties with new values
        newProperties['_bundle_id'] = bundleId;
        newProperties['_bundle_group'] = bundleGroupId;
        newProperties['_bundle_size'] = itemSize;
        newProperties['_bundle_tier'] = bundleTier;
        newProperties['_bundle_price'] = itemPrice;
        newProperties['_bundle_total'] = bundlePrice.toString();
        newProperties['_bundle_item_index'] = (i + 1).toString();
        newProperties['_bundle_colors'] = colors.join(', ');
        newProperties['_bundle_color'] = itemColor;
        newProperties['_bundle_sizes'] = sizes.join(', ');
        
        itemsToReAdd.push({
          id: item.variant_id,
          quantity: item.quantity || 1,
          properties: newProperties
        });
        
        // Get line number (1-indexed) - find the item's position in the original cart
        const lineIndex = cart.items.findIndex(cartItem => 
          cartItem.variant_id === item.variant_id && 
          cartItem.key === item.key
        );
        if (lineIndex !== -1) {
          linesToRemove.push(lineIndex + 1);
        }
      }
      
      // Remove original items in reverse order
      for (let i = linesToRemove.length - 1; i >= 0; i--) {
        const lineNumber = linesToRemove[i];
        
        const removeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: lineNumber, quantity: 0 })
        });
        
        if (!removeResponse.ok) {
          throw new Error(`Failed to remove line ${lineNumber}`);
        }
        
        // Verify removal completed by checking cart (minimal delay for API rate limiting)
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
      
      // Re-add items with bundle properties
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsToReAdd })
      });
      
      if (!addResponse.ok) {
        throw new Error('Failed to re-add items with bundle properties');
      }
      
      // Use discount code from config if available, otherwise generate it (match serverless function format)
      let discountCode = bundleConfig.discountCode;
      if (!discountCode) {
        // Fallback: format group ID to match serverless function (uppercase, preserve dashes, truncate to 20)
        const formattedGroupId = bundleGroupId
          .toString()
          .toUpperCase()
          .substring(0, 20);
        discountCode = `BDL-${bundleTier}P-${formattedGroupId}`;
        console.warn('Cart: Using fallback discount code generation (bundleConfig.discountCode was missing)');
      }
      
      // IMPORTANT: Before applying discount, check if there are any sale items in the cart
      // that are in the same collection. The discount code applies to the entire collection,
      // so if there are sale items in the same collection, they would also get the discount.
      
      // Skip discount if explicitly requested (e.g., when sale items detected before grouping)
      let shouldSkipDiscount = options.skipDiscountIfSaleItems === true;
      
      if (!shouldSkipDiscount) {
        // Get current cart state after grouping
        const currentCartResponse = await fetch('/cart.js');
        const currentCart = currentCartResponse.ok ? await currentCartResponse.json() : null;
        
        if (currentCart) {
          // Find first bundled item to check collection
          const firstBundledItem = currentCart.items.find(item => 
            item.properties && (item.properties._bundle_id || item.properties._bundle_group)
          );
          
          if (firstBundledItem) {
            const bundleProductTitle = firstBundledItem.product_title;
            
            // Check if any non-bundle items from the same product/collection are on sale
            const nonBundleItems = currentCart.items.filter(item => {
              const isBundleItem = item.properties && (item.properties._bundle_id || item.properties._bundle_group);
              return !isBundleItem;
            });
            
            const hasSaleItemInSameCollection = nonBundleItems.some(item => {
              // Same product = same sibling collection
              if (item.product_title === bundleProductTitle) {
                return this.checkItemOnSale(item);
              }
              return false;
            });
            
            if (hasSaleItemInSameCollection) {
              shouldSkipDiscount = true;
              console.log('Cart: Sale items in same collection:', nonBundleItems.filter(item => {
                if (item.product_title === bundleProductTitle) {
                  return this.checkItemOnSale(item);
                }
                return false;
              }).map(item => ({
                product: item.product_title,
                variant: item.variant_title
              })));
            }
          }
        }
      } else {
        console.log('Cart: ⚠️ Sale items detected before grouping - skipping bundle discount application');
      }
      
      // If we should skip discount, don't apply it but still return true (items were grouped)
      if (shouldSkipDiscount) {
        return true;
      }
      
      // Apply discount code and wait for it to take effect
      if (window.bundleDiscountAPI && typeof window.bundleDiscountAPI.applyDiscountCode === 'function') {
        const result = await window.bundleDiscountAPI.applyDiscountCode(discountCode);
        if (result) {
          // Wait for discount to propagate using polling instead of fixed delay
          const discountResult = await this.waitForDiscountApplication(discountCode, 5, 300);
          
          if (discountResult.success && discountResult.cart) {
            // Discount verified successfully
          } else {
            console.warn(`Cart: ⚠️ Discount code "${discountCode}" present but discount_applications is empty`);
            console.warn('Cart: This usually means:');
            console.warn('  1. The discount code doesn\'t exist in Shopify');
            console.warn('  2. The discount code exists but items don\'t match the discount conditions');
            console.warn('  3. The items are not in the collection specified by the discount');
            
            const verifyCart = discountResult.cart || (await fetch('/cart.js').then(r => r.ok ? r.json() : null));
            if (verifyCart) {
              console.warn('Cart: Items in cart:', verifyCart.items?.map(i => ({
                product: i.product_title,
                variant: i.variant_id,
                collection: i.product_collections || 'unknown'
              })) || 'no items');
            }
          }
        } else {
          console.warn(`Cart: Failed to apply discount code: ${discountCode}. Items still grouped.`);
        }
      }
      
      return true; // Items were grouped even if discount application failed
      
    } catch (error) {
      console.error('Cart: Error detecting/grouping bundle items:', error);
      return false;
    }
  }
  
  /**
   * Find bundle collection configuration for a product
   */
  async findBundleCollectionForProduct(item) {
    try {
      // Get product handle from URL (most reliable)
      let productHandle = '';
      
      // Extract from URL first (most reliable)
      if (item.url) {
        const urlMatch = item.url.match(/\/products\/([^\/\?]+)/);
        if (urlMatch) {
          productHandle = urlMatch[1];
        }
      }
      
      // Fallback to other sources
      if (!productHandle) {
        productHandle = item.handle || item.product_handle || '';
      }
      
      if (!productHandle) {
        return null;
      }
      
      // Generate multiple possible collection handle patterns from product title and handle
      const productTitle = item.product_title || '';
      const possibleHandles = this.generateCollectionHandlePatterns(productHandle, productTitle);
      
      // Try each pattern
      for (const handle of possibleHandles) {
        const config = await this.getBundleConfigFromCollection(handle);
        if (config) {
          return config;
        }
      }
      
      return null;
      
    } catch (error) {
      console.error('Cart: Error finding bundle collection:', error);
      return null;
    }
  }
  
  /**
   * Generate possible collection handle patterns from product handle and title
   */
  generateCollectionHandlePatterns(productHandle, productTitle) {
    const patterns = [];
    
    // Pattern 1-3: Direct from handle
    patterns.push(`${productHandle}-sibling`);
    patterns.push(`${productHandle}-siblings`);
    
    // Pattern 4-6: Remove common suffixes and add sibling
    const handleVariations = [
      productHandle.replace(/-bra$|-brief$|-panties$|^-bra-|^-brief-|^-panties-/i, ''),
      productHandle.replace(/-push-up-bra$|-push-up$|^-push-up-/i, ''),
      productHandle.replace(/-u-plunge-bra$|-u-plunge$|^-u-plunge-/i, '')
    ];
    
    for (const handleVar of handleVariations) {
      if (handleVar && handleVar !== productHandle) {
        patterns.push(`${handleVar}-sibling`);
        patterns.push(`${handleVar}-siblings`);
      }
    }
    
    // Pattern 7+: From product title (normalized)
    if (productTitle) {
      // Full normalized title
      let normalizedTitle = productTitle
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/--+/g, '-')
        .replace(/^-|-$/g, '');
      
      if (normalizedTitle && normalizedTitle !== productHandle) {
        patterns.push(`${normalizedTitle}-sibling`);
        patterns.push(`${normalizedTitle}-siblings`);
        
        // Try removing "Le" prefix
        const withoutLe = normalizedTitle.replace(/^le-/, '');
        if (withoutLe !== normalizedTitle) {
          patterns.push(`${withoutLe}-sibling`);
          patterns.push(`${withoutLe}-siblings`);
        }
        
        // Try removing common descriptive words
        const withoutCommonWords = normalizedTitle
          .replace(/\b(le|la|the|micro|stretch|push|up|bra|brief|panties)\b/g, '')
          .replace(/\s+/g, '-')
          .replace(/--+/g, '-')
          .replace(/^-|-$/g, '');
        
        if (withoutCommonWords && withoutCommonWords !== normalizedTitle && withoutCommonWords.length > 3) {
          patterns.push(`${withoutCommonWords}-sibling`);
        }
      }
    }
    
    // Remove duplicates and empty strings
    const uniquePatterns = [...new Set(patterns)].filter(Boolean);
    
    return uniquePatterns;
  }
  
  /**
   * Find bundle configuration from embedded cart data
   * Uses bundle configuration injected by Liquid template
   */
  async findBundleConfigByTestingDiscountCodes(item, quantity) {
    try {
      // Get variant ID to match with embedded config
      const variantId = item.variant_id;
      
      if (!variantId) {
        return null;
      }
      
      // Read bundle configuration from embedded JSON
      const configScript = document.getElementById('cart-bundle-config');
      if (!configScript) {
        return null;
      }
      
      let bundleConfigData;
      try {
        bundleConfigData = JSON.parse(configScript.textContent);
      } catch (e) {
        console.error('Cart: Failed to parse bundle configuration data:', e);
        return null;
      }
      
      // Find the product configuration for this variant
      const productConfig = bundleConfigData.products.find(p => 
        p.variantId === variantId || p.variantId === parseInt(variantId)
      );
      
      if (!productConfig) {
        return null;
      }
      
      // Check if bundle is enabled
      if (!productConfig.hasBundle || !productConfig.bundleConfig) {
        return null;
      }
      
      const bundleConfig = productConfig.bundleConfig;
      
      // Find applicable tier for this quantity
      const applicableTier = bundleConfig.tiers
        .filter(tier => tier.quantity <= quantity)
        .sort((a, b) => b.quantity - a.quantity)[0]; // Get highest tier that applies
      
      if (!applicableTier) {
        return null;
      }
      
      // Use the tier that matches the quantity (or closest lower tier)
      const tierQuantity = applicableTier.quantity;
      const tierPrice = applicableTier.price;
      
      // Generate group ID from collection handle (match serverless function format - preserve dashes)
      const rawGroupId = bundleConfig.groupId;
      
      const groupId = rawGroupId
        .toString()
        .toUpperCase()
        .substring(0, 20); // Do NOT remove dashes - match serverless function format
      
      const discountCode = `BDL-${tierQuantity}P-${groupId}`;
      
      return {
        handle: bundleConfig.collectionHandle,
        groupId: bundleConfig.groupId,
        tier: tierQuantity,
        discountCode: discountCode,
        price: tierPrice
      };
      
    } catch (error) {
      console.error('Cart: Error finding bundle config from embedded data:', error);
      return null;
    }
  }

  /**
   *  Debounced onChange event handler. Updates the cart when the user stops clicking quantity buttons.
   * @param {*} event
   */
  onChange(event) {
    this.updateQuantity(event.target.dataset.index, event.target.value, document.activeElement.getAttribute('name'));
  }

  /**
   * Returns an array of sections to render when the cart is updated. Limit: 5 sections.
   * Does not make the request to the server, only returns the section names to render.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Array} Array of sections to render
   */
  getSectionItemsToRender() {
    return ['cart-items', 'cart-shipping-notification', 'cart-recommendations', 'cart-note', 'cart-footer'];
  }

  /**
   * This function is always called when the cart is updated.
   * @param {string} json
   */
  updateCartSections (newHTML) {
    // Queue cart updates if one is already in progress
    if (this.isUpdatingCart) {
      this.pendingCartUpdate = newHTML;
      return;
    }
    
    this.isUpdatingCart = true;
    
    try {
      this.getSectionItemsToRender().forEach((item) => {
        const elementToReplace = document.getElementById(item);

        if (!elementToReplace) return;

        // replace old with new
        elementToReplace.innerHTML = newHTML.getElementById(item).innerHTML;

        if (item === 'cart-shipping-notification') {
          this.updateShippingNotification('cart-shipping-notification');
        }
      });
      
      // Process any pending update
      if (this.pendingCartUpdate) {
        const pending = this.pendingCartUpdate;
        this.pendingCartUpdate = null;
        // Recursively process pending update
        setTimeout(() => {
          this.updateCartSections(pending);
        }, 50);
      }
    } finally {
      this.isUpdatingCart = false;
      
      // After cart sections are updated, check for bundles (for drawer and cart page)
      const isCartPage = window.location.pathname === '/cart';
      const isDrawer = this.closest('[data-drawer]') !== null;
      
      if (isCartPage || isDrawer) {
        // Small delay to ensure DOM is fully updated
        setTimeout(() => {
          this.checkAndApplyBundleDiscount();
        }, 200);
      }
    }
  }

  /**
   * This function handles multi-currency support for the shipping notification bar.
   * This is called when item is added to cart or when the cart is updated.
   * @param {string} selector
   * @param {string*} json - Cart JSON
   */
  updateShippingNotification(selector) {
    const shippingNotificationWrapper = document.getElementById(selector);

    if (!shippingNotificationWrapper) {
      return;
    }

    const shippingNotification = shippingNotificationWrapper.querySelector('[data-free-shipping-notification]');

    let cartTotal = parseInt(shippingNotification.dataset.cartTotal, 10),
        freeShippingTreshold = parseInt(shippingNotification.dataset.threshold,10),
        shopCurrency = shippingNotification.dataset.shopCurrency,
        targetCurrency = shippingNotification.dataset.targetCurrency;

    if (shopCurrency !== targetCurrency) {
      cartTotal = Math.round(cartTotal / (parseFloat(Shopify.currency.rate) || 1 ));
    }

    if (cartTotal > freeShippingTreshold) {
      shippingNotification.classList.add('threshold-reached')
      shippingNotification.querySelector('span').innerHTML = window.cartStrings.shipping_free;
    } else {
      let missingAmount = freeShippingTreshold - cartTotal,
          message = window.cartStrings.shipping_not_free.replace('[amount]', Woolman.Utils.formatMoney(missingAmount));

      shippingNotification.classList.remove('threshold-reached')
      shippingNotification.querySelector('span').innerHTML = message;
    }
  }

  /**
   * Fetches fresh sections from the server and updates the cart.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @returns {Promise} Promise that resolves when the cart is updated
   */
  async renderItems() {
    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateCartSections(html);
      })
      .catch((e) => {
        console.error(e);
      });
  }
  
  /**
   * Update cart sections after bundle grouping - smooth update without full refresh
   */
  async updateCartAfterGrouping() {
    try {
      // Fetch updated cart sections
      const response = await fetch(`${routes.cart_url}?section_id=main-cart`);
      if (!response.ok) {
        throw new Error('Failed to fetch cart sections');
      }
      
      const htmlText = await response.text();
      const html = new DOMParser().parseFromString(htmlText, 'text/html');
      
      // Update cart sections smoothly (this updates cart-items, cart-footer, etc.)
      this.updateCartSections(html);
      
      // Also update bundle configuration data if it exists
      const newConfigScript = html.getElementById('cart-bundle-config');
      const oldConfigScript = document.getElementById('cart-bundle-config');
      if (newConfigScript && oldConfigScript) {
        oldConfigScript.textContent = newConfigScript.textContent;
      }
      
      // Verify discount is applied by checking cart
      const cartCheckResponse = await fetch('/cart.js');
      if (cartCheckResponse.ok) {
        const cartData = await cartCheckResponse.json();
        if (!(cartData.discount_codes && cartData.discount_codes.length > 0)) {
          console.warn('Cart: ⚠️ Discount code not found in cart after update');
        }
      }
      
    } catch (error) {
      console.error('Cart: Error updating cart after grouping:', error);
      // Fallback to full refresh if smooth update fails
      await this.renderItems();
    }
  }

  /**
   * Fetches fresh sections from the server and updates the cart. This is called when item is added to cart.
   * @see https://shopify.dev/api/ajax/reference/cart#bundled-section-rendering
   * @param {boolean} skipDrawer - Whether to skip opening the cart drawer
   * @returns {Promise} Promise that resolves when the cart is updated
   */
  async renderAndShowItems(skipDrawer = false) {
    const parentDrawer = this.closest('[data-drawer]');

    fetch(`${routes.cart_url}?section_id=main-cart`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateCartSections(html);

        // Skip opening drawer if requested (for upsell products)
        if (!parentDrawer || skipDrawer) return;
        Woolman.ModalsAndDrawers.showModalOrDrawer(parentDrawer.getAttribute('id'))
      })
      .catch((e) => {
        console.error(e);
      });
  }

  /**
   * Updates the quantity of a line item in the cart. This is called when the user changes the quantity of an item in the cart.
   * @param {*} line
   * @param {*} quantity
   * @param {*} name
   */
  async updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    fetch(`${routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
      body: JSON.stringify({
        line: line,
        quantity: quantity,
        sections: 'main-cart',
        sections_url: window.location.pathname,
      }),
    })
    .then((response) => response.json())
    .then((responseJson) => {

      const quantityElement = document.getElementById(`Quantity-${line}`);
      const items = document.querySelectorAll('.cart-item');

      if (responseJson.errors) {
        quantityElement.value = quantityElement.getAttribute('value');
        this.updateLiveRegions(line, responseJson.errors);
        return;
      }

      const event = new CustomEvent('cart:update', { detail: responseJson,});
      document.dispatchEvent(event);

      // Render new items according to response
      const html = new DOMParser().parseFromString(responseJson.sections['main-cart'], 'text/html');
      this.updateCartSections(html);

      const lineItem = document.getElementById(`CartItem-${line}`);
      if (lineItem && lineItem.querySelector(`[name="${name}"]`)) lineItem.querySelector(`[name="${name}"]`).focus();
    })
    .catch((error) => {
      console.error(error);
    })
    .finally(() => {
      this.disableLoading();
    });
  }

  /**
   * Updates the live regions for the cart.
   * @param {*} line
   * @param {*} message
   */
  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) {
      lineItemError.querySelector('.cart-item__error-text').innerHTML = message;
      lineItemError.classList.remove('hide');
    }

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  enableLoading(line) {
    this.classList.add('is-processing');
    this.querySelectorAll(`#CartItem-${line} .loading-overlay`).forEach((overlay) => overlay.classList.remove('hidden'));
    document.activeElement.blur();
  }

  disableLoading() {
    this.classList.remove('is-processing');
    this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
  }

  /**
   * Handle bundle removal
   */
  async handleBundleRemoval(button) {
    // Prevent removal during other bundle operations
    if (this.isProcessingBundle) {
      alert('Please wait for the current operation to complete before removing items.');
      return;
    }
    
    const bundleId = button.getAttribute('data-bundle-id');
    
    if (!bundleId) {
      console.error('No bundle ID found on button');
      return;
    }
    
    // Confirm removal
    if (!confirm('Remove this bundle from your cart?')) {
      return;
    }
    
    // Set processing flag to prevent concurrent operations
    this.isProcessingBundle = true;
    
    // Show loading
    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Removing...';
    
    try {
      // Get all items with this bundle_id
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }
      const cart = await response.json();
      
      const bundleItems = cart.items.filter(item => 
        item.properties && item.properties._bundle_id === bundleId
      );
      
      if (bundleItems.length === 0) {
        throw new Error('No bundle items found');
      }
      
      // Get bundle group ID to find discount code
      const bundleGroupId = bundleItems[0]?.properties?._bundle_group || '';
      const bundleTier = bundleItems[0]?.properties?._bundle_size || '';
      
      // Collect line numbers of bundle items (1-indexed)
      const bundleLineNumbers = [];
      cart.items.forEach((item, index) => {
        if (item.properties && item.properties._bundle_id === bundleId) {
          bundleLineNumbers.push(index + 1);
        }
      });
      
      // Remove items one by one in reverse order (to avoid line number shifting)
      // Use /cart/change.js endpoint which is more reliable
      for (let i = bundleLineNumbers.length - 1; i >= 0; i--) {
        const lineNumber = bundleLineNumbers[i];
        
        const changeResponse = await fetch(window.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            line: lineNumber,
            quantity: 0,
          }),
        });
        
        if (!changeResponse.ok) {
          const changeErrorText = await changeResponse.text();
          let changeError;
          try {
            changeError = JSON.parse(changeErrorText);
          } catch {
            changeError = { errors: changeErrorText || 'Unknown error' };
          }
          console.error(`Failed to remove line ${lineNumber}:`, changeError);
          throw new Error(`Failed to remove bundle item at line ${lineNumber}`);
        }
        
        await changeResponse.json();
        
        // Minimal delay between removals to avoid rate limiting (only if more items to remove)
        if (i > 0) {
          await new Promise(resolve => setTimeout(resolve, 150));
        }
      }
      
      // Remove discount code if it exists
      if (bundleGroupId && bundleTier && window.bundleDiscountAPI && typeof window.bundleDiscountAPI.removeDiscountCode === 'function') {
        try {
          // Format discount code name (matching serverless function format)
          const bundleGroupIdFormatted = bundleGroupId
            .toString()
            .toUpperCase()
            .substring(0, 20);
          const discountCode = `BDL-${bundleTier}P-${bundleGroupIdFormatted}`;
          
          await window.bundleDiscountAPI.removeDiscountCode(discountCode);
        } catch (discountError) {
          console.warn('Failed to remove discount code (may not be applied or will auto-remove):', discountError);
          // Continue anyway - discount will be removed automatically if no qualifying items remain
        }
      }
      
      // Refresh cart display
      await this.renderItems();
      
    } catch (error) {
      console.error('Error removing bundle:', error);
      alert('Failed to remove bundle. Please try again.');
      button.disabled = false;
      button.textContent = originalText;
    } finally {
      // Always release the lock
      this.isProcessingBundle = false;
    }
  }
  
  /**
   * Intercept checkout button to ensure bundle discount is applied before checkout
   */
  async interceptCheckoutButton() {
    // Wait for checkout button to appear in DOM using polling
    // Use a more comprehensive selector to catch all checkout buttons (cart page and drawer)
    const checkoutButtons = document.querySelectorAll(
      'button[name="checkout"], button[type="submit"][name="checkout"], [data-checkout-button], form#cart button[type="submit"]'
    );
    
    if (checkoutButtons.length === 0) {
      // If no buttons found, wait a bit and try again
      const checkoutButton = await this.waitForDOMElement(
        'button[name="checkout"], button[type="submit"][name="checkout"], [data-checkout-button], form#cart button[type="submit"]', 
        2000
      );
      
      if (checkoutButton) {
        this.attachCheckoutListener(checkoutButton);
      } else {
        console.warn('Cart: Checkout button not found - will not intercept checkout');
      }
    } else {
      // Intercept all found checkout buttons
      checkoutButtons.forEach(button => {
        this.attachCheckoutListener(button);
      });
    }
  }
  
  /**
   * Attach checkout listener to a button (helper to avoid duplicates)
   */
  attachCheckoutListener(button) {
    // Check if already intercepted (by checking for a data attribute)
    if (button.dataset.bundleIntercepted === 'true') {
      return;
    }
    
    // Mark button as intercepted
    button.dataset.bundleIntercepted = 'true';
    
    // Add click listener
    button.addEventListener('click', async (e) => {
      e.preventDefault();
      e.stopPropagation();
      await this.ensureDiscountBeforeCheckout();
    });
  }
  
  /**
   * Ensure bundle discount is applied before proceeding to checkout
   */
  async ensureDiscountBeforeCheckout() {
    // Prevent multiple concurrent checkout attempts
    if (this.isProcessingCheckout) {
      return;
    }
    
    this.isProcessingCheckout = true;
    
    // Disable checkout button immediately to prevent multiple clicks
    this.setCheckoutButtonState(true, 'Preparing checkout...');
    
    try {
      // First, wait for any ongoing bundle processing to complete
      if (this.isProcessingBundle) {
        const bundleCompleted = await this.waitForBundleProcessing(10000);
        if (!bundleCompleted) {
          console.warn('Cart: Bundle processing timeout, proceeding with checkout anyway');
        }
      }
      
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        console.warn('Cart: Failed to fetch cart before checkout');
        this.proceedToCheckout();
        return;
      }
      
      const cart = await cartResponse.json();
      
      // Check if there are bundle items that need discount
      const bundleItems = cart.items.filter(item => item.properties && item.properties._bundle_id);
      
      if (bundleItems.length === 0) {
        this.proceedToCheckout();
        return;
      }
      
      // Check if discount is already applied and working
      const hasWorkingDiscount = cart.discount_applications && 
                                  cart.discount_applications.length > 0 &&
                                  cart.total_price < cart.original_total_price;
      
      if (hasWorkingDiscount) {
        // Verify the discount code is in the cart
        const bundleDiscountCode = cart.discount_codes?.find(dc => {
          const codeUpper = dc.code.toUpperCase();
          return codeUpper.includes('BDL-') && codeUpper.includes('P');
        });
        
        if (bundleDiscountCode) {
          this.proceedToCheckout(bundleDiscountCode.code);
          return;
        }
      }
      
      // Discount not working - try to apply it
      const firstBundleItem = bundleItems[0];
      const bundleGroupId = firstBundleItem.properties._bundle_group || firstBundleItem.properties._bundle_id || '';
      const bundleTier = firstBundleItem.properties._bundle_tier || '';
      
      if (!bundleGroupId || !bundleTier) {
        console.warn('Cart: Missing bundle properties, proceeding to checkout without discount');
        this.proceedToCheckout();
        return;
      }
      
      // Generate discount code (match serverless function format - preserve dashes)
      const bundleGroupIdFormatted = bundleGroupId
        .toString()
        .toUpperCase()
        .substring(0, 20);
      
      const discountCode = `BDL-${bundleTier}P-${bundleGroupIdFormatted}`;
      
      // Apply discount code directly
      const applyResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: discountCode })
      });
      
      if (applyResponse.ok) {
        // Verify discount was applied using polling instead of fixed delay
        const discountResult = await this.waitForDiscountApplication(discountCode, 3, 300);
        if (discountResult.success) {
          this.proceedToCheckout(discountCode);
          return;
        } else {
          console.warn('Cart: Discount code applied but not verified before checkout');
        }
      }
      
      // If discount application failed, proceed anyway
      console.warn('Cart: Failed to apply discount before checkout, proceeding anyway');
      this.proceedToCheckout(discountCode);
      
    } catch (error) {
      console.error('Cart: Error ensuring discount before checkout:', error);
      this.proceedToCheckout();
    } finally {
      // Re-enable checkout button
      this.setCheckoutButtonState(false);
      
      // Release lock after a short delay to prevent rapid re-clicks
      setTimeout(() => {
        this.isProcessingCheckout = false;
      }, 1000);
    }
  }
  
  /**
   * Proceed to checkout (submit form normally - discount should be in cart state)
   */
  proceedToCheckout(discountCode = null) {
    // If discount code provided, ensure it's applied first
    if (discountCode) {
      // Apply discount code one more time to ensure it's in cart state
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: discountCode })
      }).then(() => {
        // Submit the form normally - discount should persist
        this.submitCheckoutForm();
      }).catch(() => {
        // If discount application fails, proceed anyway
        this.submitCheckoutForm();
      });
    } else {
      // Submit form normally
      this.submitCheckoutForm();
    }
  }
  
  /**
   * Submit the checkout form (normal form submission)
   */
  submitCheckoutForm() {
    const cartForm = document.querySelector('form#cart');
    if (cartForm) {
      // Create a submit button to trigger form submission
      const submitButton = document.createElement('button');
      submitButton.type = 'submit';
      submitButton.name = 'checkout';
      submitButton.className = 'submit-button-hidden';
      cartForm.appendChild(submitButton);
      submitButton.click();
      cartForm.removeChild(submitButton);
    } else {
      // Fallback: redirect to checkout
      window.location.href = '/checkout';
    }
  }
}
customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define('cart-note', class CartNote extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('keyup', debounce((event) => {
        const body = JSON.stringify({ note: event.target.value });
        fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
           'Content-Type': 'application/json',
            Accept: `application/json`,
          },
          ...{ body }
        });
      }, 300))
    }
  });
};

if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.loadRecommendations();
      document.addEventListener('ajaxProduct:added', this.loadRecommendations.bind(this))
    }

    async loadRecommendations() {
      if (!this.dataset.productId || this.dataset.productId === '') return;

      try {
        const url = new URL(location.origin + routes.product_recommendations_url + '.json')
        url.searchParams.set('product_id', this.dataset.productId)
        url.searchParams.set('limit', this.dataset.limit || 10)

        const res = await fetch(url)
        const { products } = await res.json();

        if (!products || products.length == 0) {
          throw 'No product recommendations found for products in the cart'
        }

        this.productsContainer = this.querySelector('[data-products-ajax]')
        this.productsContainer.innerHTML = '';

        products.forEach((product) => {

          const template = this.querySelector('#template-recommendation-item').content.firstElementChild.cloneNode(true);
          const filename = product.featured_image.split('/').pop();
          const lastDotIndex = filename.lastIndexOf('.');
          const newFilename = `${filename.substring(0, lastDotIndex)}_300x${filename.substring(lastDotIndex)}`;
          const url = product.featured_image.replace(filename, newFilename);

          template.querySelector('img').setAttribute('src', url);
          template.querySelector('.card-media').setAttribute('href', location.origin + product.url);
          template.setAttribute('title', product.title);
          template.querySelector('.card-title a').textContent = product.title;
          template.querySelector('.card-title a').setAttribute('href', location.origin + product.url);

          this.productsContainer.appendChild(template);
        })
      } catch(error) {
        this.setAttribute('hidden', true)
        console.warn(error)
      }
    }
  })
}